import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useNavigate } from 'react-router-dom'
import { cn, formatCurrency, formatRelativeTime } from '@/lib/utils'
import { Badge } from '@/components/ui/Badge'
import { UserAvatar } from '@/components/ui/Avatar'
import type { Opportunity } from '@/types'
import { Building2, Calendar, DollarSign, GripVertical, User } from 'lucide-react'

interface KanbanCardProps {
  opportunity: Opportunity
  isDragging?: boolean
}

export function KanbanCard({ opportunity, isDragging }: KanbanCardProps) {
  const navigate = useNavigate()

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: opportunity.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const handleClick = () => {
    navigate(`/opportunities/${opportunity.id}`)
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'kanban-card group',
        (isDragging || isSortableDragging) && 'dragging opacity-50'
      )}
      onClick={handleClick}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>

      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <UserAvatar
          name={opportunity.owner_name || 'N/A'}
          size="sm"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h4 className="font-medium text-sm truncate flex-1">{opportunity.name}</h4>
            {opportunity.stage === 'won' && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-green-600 text-white whitespace-nowrap">Ganho</span>
            )}
            {opportunity.stage === 'lost' && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-gray-500 text-white whitespace-nowrap">Perdido</span>
            )}
            {opportunity.lead_company_is_client === true && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-emerald-500 text-white whitespace-nowrap">Base</span>
            )}
            {opportunity.lead_company_is_client === false && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-blue-500 text-white whitespace-nowrap">Novo</span>
            )}
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Building2 className="h-3 w-3" />
            <span className="truncate">{opportunity.lead_company_name || 'Sem empresa'}</span>
          </div>
        </div>
      </div>

      {/* Contact */}
      {opportunity.contact_name && (
        <p className="text-xs text-muted-foreground mb-2 truncate flex items-center gap-1">
          <User className="h-3 w-3" />
          {opportunity.contact_name}
        </p>
      )}

      {/* Products */}
      {opportunity.products && opportunity.products.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {opportunity.products.slice(0, 2).map((p, i) => (
            <Badge key={i} variant="outline" className="text-xs">
              {p.name || p.product_id}
            </Badge>
          ))}
          {opportunity.products.length > 2 && (
            <Badge variant="outline" className="text-xs">
              +{opportunity.products.length - 2}
            </Badge>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
        <div className="flex items-center gap-1">
          <DollarSign className="h-3 w-3" />
          <span className="font-medium text-foreground">
            {formatCurrency(opportunity.value)}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          <span>{formatRelativeTime(opportunity.updated_at)}</span>
        </div>
      </div>

      {/* Probability Bar */}
      <div className="mt-2">
        <div className="flex justify-between text-xs mb-1">
          <span className="text-muted-foreground">Probabilidade</span>
          <span className="font-medium">{opportunity.probability}%</span>
        </div>
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all"
            style={{ width: `${opportunity.probability}%` }}
          />
        </div>
      </div>
    </div>
  )
}
